/**
 * Custom hook for managing message configuration info (current and historical)
 *
 * Extracted from ChatPanel.tsx to reduce complexity
 */

import { useState, useEffect, useRef } from 'react';
import { apiService } from '../../../services/api';

export interface ChatMessage {
  id: string;
  isUser: boolean;
  isStreaming?: boolean;
  selectedDocuments?: string[];
}

export interface UseMessageConfigProps {
  messages: ChatMessage[];
  selectedDocuments: string[];
  conversationId?: string;
  isTyping?: boolean;
  enabled: boolean;
  refreshTrigger?: number; // Nuevo: contador para forzar recarga
}

export interface UseMessageConfigReturn {
  configInfo: any;
  historicalConfigInfo: Record<string, any>;
  loadingConfigInfo: Record<string, boolean>;
  loadHistoricalConfig: (messageId: string, documentIds: string[]) => Promise<void>;
}

/**
 * Hook to manage current and historical message configuration
 */
export function useMessageConfig({
  messages,
  selectedDocuments,
  conversationId,
  isTyping = false,
  enabled,
  refreshTrigger = 0
}: UseMessageConfigProps): UseMessageConfigReturn {
  const [configInfo, setConfigInfo] = useState<any>(null);
  const [historicalConfigInfo, setHistoricalConfigInfo] = useState<Record<string, any>>({});
  const [loadingConfigInfo, setLoadingConfigInfo] = useState<Record<string, boolean>>({});
  // Refs must be declared before effects to maintain hook order
  const isMountedRef = useRef(false);
  const notFoundCacheRef = useRef<Set<string>>(new Set());

  // Load current configuration
  useEffect(() => {
    if (!enabled) {
      setConfigInfo(null);
      return;
    }

    if (!selectedDocuments.length && !conversationId) {
      setConfigInfo(null);
      return;
    }

    const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
    const shouldLoad = messages.length === 0 ||
                      (lastMessage && !lastMessage.isUser && !lastMessage.isStreaming && !isTyping);

    if (!shouldLoad) {
      return;
    }

    const loadConfigInfo = async () => {
      try {
        const response = await apiService.getConfigInfo({
          document_ids: selectedDocuments,
          conversation_id: conversationId,
        });

        if (response.success && response.config_info) {
          setConfigInfo(response.config_info);
        } else {
          setConfigInfo(null);
        }
      } catch (error) {
        console.error('Error loading config info:', error);
        setConfigInfo(null);
      }
    };

    const timeoutId = setTimeout(() => {
      loadConfigInfo();
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [selectedDocuments, conversationId, messages.length, enabled, isTyping, refreshTrigger]);

  // Mark component as mounted
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Load historical configurations
  useEffect(() => {
    if (!enabled || messages.length < 2) return;

    // Delay loading to avoid race conditions with backend message persistence
    const timeoutId = setTimeout(() => {
      if (!isMountedRef.current) {
        return;
      }

      const recentMessages = messages.slice(-10);

      recentMessages.forEach((message) => {
        if (message.isUser && message.selectedDocuments && message.selectedDocuments.length > 0) {
          const messageId = message.id;

          // Skip temporary messages
          if (messageId && messageId.startsWith('temp-')) {
            return;
          }

          // Skip very recent messages (< 2 seconds old) to avoid race conditions
          // Backend needs time to persist the message before we can query its config
          if (messageId && messageId.startsWith('streaming-')) {
            return;
          }

          if (!historicalConfigInfo[messageId] && !loadingConfigInfo[messageId]) {
            loadHistoricalConfig(messageId, message.selectedDocuments);
          }
        }
      });
    }, 500); // Increased delay to 500ms to give backend time to persist messages

    return () => clearTimeout(timeoutId);
  }, [enabled, messages.length]);

  const loadHistoricalConfig = async (messageId: string, documentIds: string[]) => {
    // No cargar config para mensajes temporales o inválidos
    if (!messageId || messageId.startsWith('temp-')) {
      console.debug('[useMessageConfig] Skipping temp/invalid message:', messageId);
      return;
    }

    // No cargar si ya está cargando o ya existe
    if (loadingConfigInfo[messageId] || historicalConfigInfo[messageId]) {
      return;
    }

    // Skip if we already know this message doesn't exist (404 cache)
    if (notFoundCacheRef.current.has(messageId)) {
      console.debug('[useMessageConfig] Skipping known non-existent message (cached 404):', messageId);
      return;
    }

    setLoadingConfigInfo(prev => ({ ...prev, [messageId]: true }));

    try {
      const response = await apiService.getConfigInfo({
        document_ids: documentIds,
        message_id: messageId,
      });

      if (response.success && response.config_info) {
        setHistoricalConfigInfo(prev => ({ ...prev, [messageId]: response.config_info }));
      }
    } catch (error: any) {
      // Silenciar error 404 (mensaje no encontrado en backend)
      // Esto es normal cuando el mensaje fue eliminado o nunca se guardó
      if (error?.message?.includes('no encontrado') || error?.status === 404) {
        console.debug('[useMessageConfig] Message not found in backend, caching as 404:', messageId);
        // Add to cache to prevent future attempts
        notFoundCacheRef.current.add(messageId);
      } else {
        console.error('Error loading historical config info:', error);
      }
    } finally {
      setLoadingConfigInfo(prev => ({ ...prev, [messageId]: false }));
    }
  };

  return {
    configInfo,
    historicalConfigInfo,
    loadingConfigInfo,
    loadHistoricalConfig
  };
}
